"use client"

import { Link, useParams, useNavigate } from "react-router-dom"
import { useApp } from "../contexts/AppContext"
import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  BiPlus,
  BiX,
  BiFile,
  BiSearch,
  BiSortAlt2,
  BiCard,
  BiListUl,
  BiArrowBack,
  BiStats,
  BiTime,
  BiFolder,
  BiInfoCircle,
  BiBriefcase,
  BiTable,
  BiRefresh,
} from "react-icons/bi"

import FeuilleModal from "../components/modals/FeuilleModal"
import FeuilleTabs from "../components/tabs/FeuilleTabs"
import FeuilleCard from "../components/cards/FeuilleCard"
import FeuilleList from "../components/lists/FeuilleList"

import AnimatedLoader from "../components/ui/AnimatedLoader"
import HeaderAlert from "../components/ui/HeaderAlert"

export default function FeuillePage() {
  const navigate = useNavigate()

  const { classeurId } = useParams()
  const { getClasseur, getFeuillesByClasseur, error, success, clearError, clearSuccess, loading } = useApp()

  // États locaux
  const [showFeuilleModal, setShowFeuilleModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("nom_feuille")
  const [sortOrder, setSortOrder] = useState("asc")
  const [viewMode, setViewMode] = useState(localStorage.getItem("view-feuille-mode") || "tabs")  // tabs | grid | list
  const [showStats, setShowStats] = useState(false)

  // Données
  const classeur = getClasseur(classeurId)
  const allFeuilles = getFeuillesByClasseur(classeurId)

  // Feuilles filtrées et triées
  const filteredAndSortedFeuilles = useMemo(() => {
    const filtered = allFeuilles.filter(
      (feuille) => feuille.nom_feuille.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feuille.libelle_feuille && feuille.libelle_feuille.toLowerCase().includes(searchTerm.toLowerCase())),
    )

    filtered.sort((a, b) => {
      let aValue = a[sortBy] || ""
      let bValue = b[sortBy] || ""

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    return filtered
  }, [allFeuilles, searchTerm, sortBy, sortOrder])

  // Statistiques
  const stats = useMemo(() => {
    const totalFeuilles = allFeuilles.length
    const activeFeuilles = allFeuilles.filter((f) => f.statut === 1).length
    const inactiveFeuilles = totalFeuilles - activeFeuilles
    const withData = allFeuilles.filter((f) => f.source_donnees === "Oui").length

    return {
      total: totalFeuilles,
      active: activeFeuilles,
      inactive: inactiveFeuilles,
      withData,
      withoutData: totalFeuilles - withData,
    }
  }, [allFeuilles])

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "n":
            e.preventDefault()
            setShowFeuilleModal(true)
            break
          case "f":
            e.preventDefault()
            document.getElementById("search-input")?.focus()
            break
          case "s":
            e.preventDefault()
            setShowStats(!showStats)
            break
        }
      }
    }

    document.addEventListener("keydown", handleKeyPress)
    return () => document.removeEventListener("keydown", handleKeyPress)
  }, [showStats])

  // Gestion d'affichage
  const handleViewFeuilleMode = (mode) => {
    setViewMode(mode)
    localStorage.setItem("view-feuille-mode", mode)
  }

  //=======================================================>
  //=======================================================>
  // Variants d'animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  }

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }

  const statsVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }


  // Gestion des erreurs
  if (!classeur) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-5"
        >
          <BiFolder className="display-1 text-danger mb-3" />
          <h3 className="text-danger">Classeur non trouvé</h3>
          <p className="text-muted mb-4">Le classeur demandé n'existe pas ou a été supprimé.</p>
          <button onClick={() => navigate("/")} className="btn btn-primary">
            <BiArrowBack className="me-1" />
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
      {/* Messages d'alerte */}
      <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

      {/* En-tête */}
      <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-3">
        {/* Breadcrumb et titre */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <nav aria-label="breadcrumb">
              <motion.ol
                className="breadcrumb mb-2"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }} >
                <li className="breadcrumb-item">
                  <Link to={`/`} className="text-decoration-none d-flex align-items-center">
                    <BiBriefcase className="me-1" /> Projets
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/projet/${classeur.id_projet}`} className="text-decoration-none d-flex align-items-center">
                    <BiFolder className="me-1" /> Classeurs
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {classeur.libelle_classeur}
                </li>
              </motion.ol>
            </nav>

            <motion.div
              className="d-flex align-items-center mb-2"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="rounded me-3 d-flex align-items-center justify-content-center"
                style={{ width: "48px", height: "48px", backgroundColor: classeur.couleur_classeur || "#6c757d" }}>
                <BiFolder className="text-white fs-4" />
              </div>
              <div>
                <h5 className="mb-0">{classeur.libelle_classeur}</h5>
                {classeur.note_classeur && <p className="text-muted mb-0 small">{classeur.note_classeur}</p>}
              </div>
            </motion.div>
          </div>

          {/* Actions principales */}
          <motion.div className="d-flex gap-2 flex-wrap" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
            <motion.button
              onClick={() => window.location.reload()}
              title="Rafraichir"
              className="btn btn-sm btn-outline-secondary d-flex align-items-center p-1 rounded-circle"
              disabled={loading}>
              <BiRefresh className="fs-5" />
            </motion.button>

            <button onClick={() => setShowStats(!showStats)} className="btn btn-sm btn-outline-secondary d-flex align-items-center"
              title="Statistiques"> <BiStats className="me-1" /> Stats
            </button>

            <div className="btn-group btn-group-sm">
              <button className={`btn ${viewMode === "tabs" ? "btn-secondary" : "btn-outline-secondary"} d-flex align-items-center`}
                onClick={() => handleViewFeuilleMode("tabs")} title="Vue onglets"> <BiTable />
              </button>

              <button className={`btn ${viewMode === "list" ? "btn-secondary" : "btn-outline-secondary"} d-flex align-items-center`}
                onClick={() => handleViewFeuilleMode("list")} title="Vue liste"> <BiListUl />
              </button>

              <button className={`btn ${viewMode === "grid" ? "btn-secondary" : "btn-outline-secondary"} d-flex align-items-center`}
                onClick={() => handleViewFeuilleMode("grid")} title="Vue grille"> <BiCard />
              </button>
            </div>

            <button onClick={() => setShowFeuilleModal(true)} className="btn btn-sm btn-success d-flex align-items-center"
              disabled={loading} title="Nouvelle feuille">
              <BiPlus className="me-1" /> Nouvelle Feuille
            </button>

            <button onClick={() => navigate(`/projet/${classeur.id_projet}`)} className="btn btn-sm btn-secondary d-flex align-items-center" title="Fermer">
              <BiX className="me-1" /> Fermer
            </button>
          </motion.div>
        </div>

        {/* Statistiques */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              variants={statsVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="card mb-3 rounded-1"
            >
              <div className="card-header bg-light d-flex align-items-center small py-1">
                <BiStats className="me-1" />
                <strong>Statistiques du classeur</strong>
              </div>
              <div className="card-body py-2">
                <div className="row text-center">
                  <div className="col-6 col-md-3">
                    <div className="border-end">
                      <div className="fs-4 fw-bold text-primary">{stats.total}</div>
                      <small className="text-muted">Total feuilles</small>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="border-end">
                      <div className="fs-4 fw-bold text-success">{stats.active}</div>
                      <small className="text-muted">Actives</small>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="border-end">
                      <div className="fs-4 fw-bold text-warning">{stats.inactive}</div>
                      <small className="text-muted">Inactives</small>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="fs-4 fw-bold text-info">{stats.withData}</div>
                    <small className="text-muted">Avec données</small>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Barre de recherche et filtres */}
        <motion.div
          className="row g-3 mb-3"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="col-md-6">
            <div className="input-group input-group-sm">
              <span className="input-group-text">
                <BiSearch />
              </span>
              <input
                id="search-input"
                type="text"
                className="form-control"
                placeholder="Rechercher une feuille... (Ctrl+F)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                  <BiX />
                </button>
              )}
            </div>
          </div>

          <div className="col-md-3">
            <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="nom_feuille">Trier par nom</option>
              <option value="libelle_feuille">Trier par description</option>
              <option value="date_insertion">Trier par date</option>
              <option value="statut">Trier par statut</option>
            </select>
          </div>

          <div className="col-md-3">
            <button
              className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              <BiSortAlt2 className="me-1" />
              {sortOrder === "asc" ? "Croissant" : "Décroissant"}
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Contenu principal */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <AnimatePresence mode="wait">
          {loading && filteredAndSortedFeuilles.length === 0 ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AnimatedLoader text="Chargement des feuilles..." />
            </motion.div>
          ) : filteredAndSortedFeuilles.length === 0 ? (
            <motion.div key="empty" variants={itemVariants} className="text-center py-5">
              {searchTerm ? (
                // État de recherche vide
                <div>
                  <BiSearch className="display-1 text-muted mb-3" />
                  <h3 className="text-muted">Aucun résultat</h3>
                  <p className="text-muted mb-4">Aucune feuille ne correspond à votre recherche "{searchTerm}"</p>
                  <button onClick={() => setSearchTerm("")} className="btn btn-outline-primary">
                    Effacer la recherche
                  </button>
                </div>
              ) : (
                // État vide initial
                <div>
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Number.POSITIVE_INFINITY,
                      repeatType: "reverse",
                    }}
                  >
                    <BiFile className="display-1 text-muted mb-3" />
                  </motion.div>
                  <h3 className="text-muted">Aucune feuille</h3>
                  <p className="text-muted mb-4">Créez votre première feuille pour commencer à organiser vos données</p>
                  <button
                    onClick={() => setShowFeuilleModal(true)}
                    className="btn btn-success d-flex align-items-center mx-auto"
                  >
                    <BiPlus className="me-1" />
                    Créer une feuille
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="content" variants={itemVariants}>
              {viewMode === "tabs" ? (
                <FeuilleTabs classeurId={classeurId} feuilles={filteredAndSortedFeuilles} />
              ) : viewMode === "grid" ? (
                <FeuilleCard classeurId={classeurId} feuilles={filteredAndSortedFeuilles} />
              ) : (
                <FeuilleList classeurId={classeurId} feuilles={filteredAndSortedFeuilles} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Informations supplémentaires */}
      <motion.div
        className="mt-3 pt-3 border-top"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}>
        <div className="row text-muted small">
          <div className="col-md-6 d-flex align-items-center justify-content-start">
            <BiInfoCircle className="me-1" />
            {filteredAndSortedFeuilles.length} feuille{filteredAndSortedFeuilles.length !== 1 ? "s" : ""}
            {searchTerm && ` (filtrée${filteredAndSortedFeuilles.length !== 1 ? "s" : ""} sur ${allFeuilles.length})`}
          </div>

          <div className="col-md-6 d-flex align-items-center justify-content-end">
            <BiTime className="me-1" />
            Dernière mise à jour: {new Date().toLocaleString("fr-FR")}
          </div>
        </div>
      </motion.div>

      {/* Modal */}
      <FeuilleModal
        isOpen={showFeuilleModal}
        onClose={() => setShowFeuilleModal(false)}
        classeurId={classeur.code_classeur}
      />
    </div>
  )
}
